import '@adornis/bh-matching-frontend-entrypoint/matching-page-admin.js';
import { initAdminRouter } from '@adornis/bh-matching-frontend-entrypoint/matching-page-admin.js';
import { mentoringDefaultFallback, mentoringDefaultRoutes } from '@adornis/bh-matching-frontend-entrypoint/router.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import '@adornis/forms/x-radiogroup.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { MatchingHautnahMenteeUser, MatchingHautnahMentorUser } from '../entities/matching-user.js';
import logo from './assets/buchungsheld_logo_small.png';
import { menteeCustomFieldMapping, mentorCustomFieldMapping } from './form-mapping.js';
import './pages/matching-hautnah-page-admin.js';
import './pages/matching-hautnah-page-timeframes.js';

const fieldsOverride = userClass => {
  if (userClass._class === MatchingHautnahMentorUser._class) {
    const registrationFields = [
      // Personal information
      'firstName',
      'lastName',
      'gender',
      'dateOfBirth',
      'age',
      'email',
      'phoneNumber',

      // Availability
      'availableDates',
      'numberOfMentees',

      // Academic information
      'studyPrograms',
      'teachingSubjects',

      // Additional information
      'nativeLanguage',
      'foreignLanguages',
      'wunschsprache',
      'genderPreference',
      'additionalNotes',

      // This must come last as specified
      'dataPrivacyConsent',
    ];

    return Object.values(MatchingHautnahMentorUser.fields)
      .filter(field => registrationFields.includes(field.name))
      .sort((a, b) => registrationFields.indexOf(a.name) - registrationFields.indexOf(b.name));
  }

  if (userClass._class === MatchingHautnahMenteeUser._class) {
    const registrationFields = [
      // Personal information
      'firstName',
      'lastName',
      'email',
      'phoneNumber',
      'dateOfBirth',
      'age',
      'gender',
      'genderPreference',
      'numberOfFellowMentees',

      // School information
      'gradeLevel',
      'schoolName',
      'customSchoolName',

      // Availability for matching days
      'availableDates',

      // Study interests
      'primaryStudyInterest',
      'additionalStudyInterests',
      'teachingSubjectInterest',
      'additionalNotes',

      // Checkboxes must come last as specified
      'singleRegistrationConfirmed',
      'dataPrivacyConsent',

      'parentalConsentDoc',
    ];

    return Object.values(MatchingHautnahMenteeUser.fields)
      .filter(field => registrationFields.includes(field.name))
      .sort((a, b) => registrationFields.indexOf(a.name) - registrationFields.indexOf(b.name));
  }

  console.log('unknown user class', userClass);
  return [];
};

const mentorRegistrationInfo = html`
  Fülle dieses Formular aus, um Dich für die
  <b>Matching Days von UNI:hautnah</b> anzumelden. <br /><br />
  Deine Anmeldung war erfolgreich, wenn Du eine Bestätigungsmail von uns bekommen hast (Achtung, die Mail kann im
  Spam-Ordner landen). Falls nötig, kannst Du Deine Anmeldedaten im Nachhinein noch ändern. Bitte überprüfe, ob die
  Bestätigungsmail ankam. Ist dies nicht der Fall, wurde eventuell eine falsche E-Mail-Adresse angegeben. <br /><br />
  Am Ende der Anmeldephase melden wir uns bei Dir und stellen den Kontakt zu einer:einem Schüler:in für Dich her.
  Abhängig von der Anzahl an Anmeldungen kann es passieren, dass mehrere Schüler:innen einem:einer Student:in zugeordnet
  werden, damit für möglichst viele ein Matching arrangiert werden kann. Du kannst bei der Anmeldung frei wählen, ob du
  einen oder mehrere Schüler begleiten möchtest. <br /><br />
  Nähere Informationen zum Projekt UNI:hautnah findest Du auf unserer Webseite
  <a href="https://uni-hautnah.weebly.com" target="_blank">www.uni-hautnah.weebly.com</a>.
`;

const menteeRegistrationInfo = html`
  Fülle dieses Formular aus, um Dich für die <b>Matching Days von UNI:hautnah</b> anzumelden.<br /><br />
  Deine Anmeldung war erfolgreich, wenn Du eine Bestätigungsmail von uns bekommen hast (Achtung, die Mail kann im
  Spam-Ordner landen). Falls nötig, kannst Du Deine Anmeldedaten im Nachhinein noch ändern. Bitte überprüfe, ob die
  Bestätigungsmail ankam. Ist dies nicht der Fall, wurde eventuell eine falsche E-Mail-Adresse angegeben.<br /><br />
  Am Ende der Anmeldephase melden wir uns bei Dir und stellen den Kontakt zu einer:einem Studierenden für Dich her.
  Abhängig von der Anzahl an Anmeldungen kann es passieren, dass mehrere Schüler:innen einem:einer Student:in zugeordnet
  werden, damit für möglichst viele ein Matching arrangiert werden kann.<br /><br />
  Nähere Informationen zum Projekt UNI:hautnah findest Du auf unserer Webseite
  <a href="https://uni-hautnah.weebly.com" target="_blank">www.uni-hautnah.weebly.com</a>.

  <p>
    Eine Übersicht der Studiengänge findet sich hier:
    <a href="https://www.uni-frankfurt.de/de/studium/studiengaenge?page=1" target="_blank"
      >www.uni-frankfurt.de/de/studium/studiengaenge?page=1</a
    >.
  </p>
`;

export const matchingConfig = {
  mentorUserClass: MatchingHautnahMentorUser,
  menteeUserClass: MatchingHautnahMenteeUser,
  mentorCustomFieldMapping: mentorCustomFieldMapping,
  menteeCustomFieldMapping: menteeCustomFieldMapping,
  logo,
  fieldsOverride,
  configOverrides: {
    ['admin-login']: {
      render: params => {
        return html` <bh-login></bh-login> `;
      },
    },
    ['signup-mentee']: {
      render: () =>
        html`
          <buddy-page-signup
            .userClass=${MatchingHautnahMenteeUser}
            page-title=${clientTranslator.translate('buddy', 'menteeSignupTitle')}
            .fieldsOverride=${fieldsOverride}
            .infoText=${menteeRegistrationInfo}
            .customFieldMapping=${menteeCustomFieldMapping}
          ></buddy-page-signup>
        `,
    },
    ['signup-mentor']: {
      render: () => {
        return html` <buddy-page-signup
          .userClass=${MatchingHautnahMentorUser}
          page-title=${clientTranslator.translate('buddy', 'mentorSignupTitle')}
          .fieldsOverride=${fieldsOverride}
          .infoText=${mentorRegistrationInfo}
          .customFieldMapping=${mentorCustomFieldMapping}
        ></buddy-page-signup>`;
      },
    },
    ['timeframes']: {
      render: () =>
        html`
          <matching-hautnah-page-timeframes institution-id="global" .userClass=${''}></matching-hautnah-page-timeframes>
        `,
    },
  },
};

export const adminRouter = initAdminRouter(matchingConfig);
adminRouter.init();

const fallback = mentoringDefaultFallback(matchingConfig);
const mainRouter = makeRouter(mentoringDefaultRoutes(matchingConfig), { fallback });
const mainComponent = webcomponent('main-component', () => mainRouter.outlet.value);
mainRouter.init();

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  override render() {
    return mainComponent.render();
  }
}
